import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Flame, Target } from "lucide-react";

const TIER = {
  5: "bg-orange-100 text-orange-700 border-orange-300",
  10: "bg-rose-100 text-rose-700 border-rose-300",
  20: "bg-violet-100 text-violet-700 border-violet-300",
};

// 🔥 badge for 5 / 10 / 20 working days in a row at or above target.
export const StreakBadge = ({ r, testid }) => {
  if (!r?.tier) return null;
  return (
    <span className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-px text-[10px] font-bold ${TIER[r.tier]}`}
      title={`${r.streak} working days in a row at or above target`} data-testid={testid}>
      <Flame className="w-3 h-3" />{r.badge}
    </span>
  );
};

const tone = (pct) => pct >= 100 ? ["bg-emerald-500", "text-emerald-700", "border-emerald-200 bg-emerald-50/60"]
  : pct >= 70 ? ["bg-amber-500", "text-amber-700", "border-amber-200 bg-amber-50/60"]
  : ["bg-rose-500", "text-rose-700", "border-rose-200 bg-rose-50/50"];

const Mini = ({ r }) => {
  const t = tone(r.pct);
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px]" data-testid={`target-team-${r.email}`}>
      <b className="text-slate-700">{r.name}</b>
      <span className="w-12 h-1.5 rounded-full bg-slate-200 overflow-hidden"><span className={`block h-full ${t[0]}`} style={{ width: `${Math.min(100, r.pct)}%` }} /></span>
      <span className={`font-bold ${t[1]}`}>{r.today}/{r.target}</span>
      <StreakBadge r={r} testid={`target-team-badge-${r.email}`} />
    </span>
  );
};

// Iter 319 — Order Register: today's parcel target card (+ team row for admins).
export const TargetCard = ({ refreshKey, isAdmin }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    axios.get(`${API}/reports/target-progress`, { headers: getAuthHeader() })
      .then(r => setD(r.data)).catch(() => {});
  }, [refreshKey]);
  if (!d || (!d.me && !(isAdmin && d.team.length))) return null;
  const me = d.me;
  const others = (d.team || []).filter(r => r.email !== me?.email);
  const t = me ? tone(me.pct) : null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="or-target-row">
      {me && (
        <div className={`inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border px-3 py-1.5 ${t[2]}`} data-testid="or-target-card">
          <Target className={`w-4 h-4 ${t[1]}`} />
          <span className="text-sm font-semibold text-slate-800" data-testid="or-target-count">Today: {me.today} / {me.target} parcels</span>
          <span className="w-28 h-2 rounded-full bg-white/80 border border-slate-200 overflow-hidden"><span className={`block h-full ${t[0]}`} style={{ width: `${Math.min(100, me.pct)}%` }} /></span>
          <span className={`text-xs font-semibold ${t[1]}`} data-testid="or-target-needed">{me.needed ? `${me.needed} to go` : "Target met!"}</span>
          {me.streak > 0 && <span className="text-[11px] text-slate-600" data-testid="or-target-streak">streak {me.streak} day{me.streak === 1 ? "" : "s"}</span>}
          <StreakBadge r={me} testid="or-target-badge" />
        </div>
      )}
      {isAdmin && others.map(r => <Mini key={r.email} r={r} />)}
    </div>
  );
};
